/* Hallmark · macrostructure: Workbench · tone: operational-editorial · anchor hue: heritage green
 * pre-emit critique: P5 H5 E4 S5 R5 V4 · contrast: pass · nav: existing admin shell · footer: none
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  Archive,
  BookOpen,
  Calendar,
  ChevronRight,
  FileText,
  Grid2X2,
  List,
  Loader2,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { StatusChip } from '@lms/ui';
import { useDeleteProgram, usePrograms } from '../hooks/usePrograms';
import { Pagination } from '../components/Pagination';
import type { Program, ProgramStatus } from '@lms/shared/types';
import { resolveMediaUrl } from '@lms/shared/utils';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import { apiBaseUrl } from '../lib/api-client';

type ProgramFilter = 'ALL' | ProgramStatus;
type ProgramView = 'cards' | 'rows';

interface ProgramPresentationProps {
  formatDate: (date: Date | string | undefined) => string;
  onDelete: (id: string) => void;
  program: Program;
  statusLabel: (status: ProgramStatus) => string;
  statusVariant: (status: ProgramStatus) => 'success' | 'warning' | 'error' | 'neutral';
}

function getStatusVariant(status: ProgramStatus): 'success' | 'warning' | 'error' | 'neutral' {
  switch (status) {
    case 'PUBLISHED':
      return 'success';
    case 'DRAFT':
      return 'warning';
    case 'ARCHIVED':
      return 'neutral';
  }
}

function getRegisteredParticipantCount(program: Program): number {
  return program.registeredParticipantCount ?? program._count?.registrations ?? 0;
}

function ProgramCover({ program }: { program: Program }) {
  return (
    <div className="relative aspect-[16/8] overflow-hidden rounded-2xl bg-surface-container-low">
      {program.coverImage ? (
        <img
          src={resolveMediaUrl(program.coverImage, apiBaseUrl)}
          alt={program.name}
          width={640}
          height={320}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full items-center justify-center bg-primary/10 text-primary">
          <BookOpen size={28} aria-hidden="true" />
        </div>
      )}
    </div>
  );
}

function ProgramCard({
  formatDate,
  onDelete,
  program,
  statusLabel,
  statusVariant,
}: ProgramPresentationProps) {
  const { t } = useLanguage();

  return (
    <article className="flex min-w-0 flex-col rounded-3xl bg-surface-container-lowest p-4 shadow-level1">
      <ProgramCover program={program} />
      <div className="flex flex-1 flex-col gap-4 px-1 pb-1 pt-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <StatusChip label={statusLabel(program.status)} variant={statusVariant(program.status)} />
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-label font-bold uppercase tracking-wider ${
              program.isRegistrationOpen
                ? 'bg-success-container text-primary'
                : 'bg-surface-container-high text-on-surface-variant'
            }`}
          >
            <Radio size={12} aria-hidden="true" />
            {program.isRegistrationOpen
              ? t(copy.programs.registrationOpen)
              : t(copy.programs.registrationClosed)}
          </span>
        </div>
        <div className="min-w-0">
          <h2 className="m-0 overflow-wrap-anywhere font-headline text-lg font-bold tracking-tight text-on-surface">
            {program.name}
          </h2>
          <p className="mt-2 line-clamp-2 min-h-10 text-sm leading-6 text-on-surface-variant">
            {program.description || t(copy.programs.noDescription)}
          </p>
        </div>
        <dl className="grid grid-cols-2 gap-3 rounded-2xl bg-surface-container-low p-3.5 text-xs">
          <div className="min-w-0">
            <dt className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
              <Bilingual {...copy.common.code} />
            </dt>
            <dd className="mt-1 truncate font-mono font-bold text-primary">{program.code}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
              <Bilingual {...copy.programs.tableHeaderEnrollments} />
            </dt>
            <dd className="mt-1 flex items-center gap-1.5 font-body font-semibold text-on-surface">
              <Users size={13} aria-hidden="true" />
              <span className="tabular-nums">{getRegisteredParticipantCount(program)}</span>
            </dd>
          </div>
          <div className="col-span-2 min-w-0">
            <dt className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
              <Bilingual {...copy.programs.tableHeaderCommencement} />
            </dt>
            <dd className="mt-1 flex items-center gap-1.5 text-on-surface">
              <Calendar size={13} className="shrink-0 text-primary" aria-hidden="true" />
              <span>{formatDate(program.startDate)}</span>
            </dd>
          </div>
        </dl>
        <div className="mt-auto flex items-center gap-2 pt-1">
          <Link
            to={`/programs/${program.id}`}
            className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-xs font-label font-bold text-on-primary no-underline transition-colors duration-150 hover:bg-primary-container focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
          >
            <Bilingual {...copy.programs.viewDetails} />
            <ChevronRight size={16} aria-hidden="true" />
          </Link>
          <button
            type="button"
            onClick={() => onDelete(program.id)}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-error transition-colors duration-150 hover:bg-error/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-error/20 active:scale-95"
            title={t(copy.programs.btnDelete)}
            aria-label={t(copy.programs.btnDelete)}
          >
            <Trash2 size={17} aria-hidden="true" />
          </button>
        </div>
      </div>
    </article>
  );
}

function ProgramRow({
  formatDate,
  onDelete,
  program,
  statusLabel,
  statusVariant,
}: ProgramPresentationProps) {
  const { t } = useLanguage();

  return (
    <article className="grid min-w-0 gap-4 rounded-2xl bg-surface-container-lowest p-4 sm:grid-cols-[5.5rem_minmax(0,1fr)_auto] sm:items-center sm:gap-5">
      <div className="hidden sm:block">
        <ProgramCover program={program} />
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip label={statusLabel(program.status)} variant={statusVariant(program.status)} />
          <span className="rounded-lg bg-primary/10 px-2 py-0.5 font-mono text-[10px] font-bold text-primary">
            {program.code}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-on-surface-variant">
            <Users size={12} aria-hidden="true" />
            <span className="tabular-nums">{getRegisteredParticipantCount(program)}</span>
          </span>
        </div>
        <h2 className="mt-3 overflow-wrap-anywhere font-headline text-base font-bold tracking-tight text-on-surface">
          {program.name}
        </h2>
        <p className="mt-1 line-clamp-1 text-xs text-on-surface-variant">
          {program.description || t(copy.programs.noDescription)}
        </p>
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-on-surface-variant">
          <Calendar size={12} className="text-primary" aria-hidden="true" />
          {formatDate(program.startDate)}
        </p>
      </div>
      <div className="flex items-center justify-between gap-2 sm:justify-end">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-label font-bold uppercase tracking-wider ${
            program.isRegistrationOpen
              ? 'bg-success-container text-primary'
              : 'bg-surface-container-high text-on-surface-variant'
          }`}
        >
          <Radio size={12} aria-hidden="true" />
          {program.isRegistrationOpen
            ? t(copy.programs.registrationOpen)
            : t(copy.programs.registrationClosed)}
        </span>
        <button
          type="button"
          onClick={() => onDelete(program.id)}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-error transition-colors duration-150 hover:bg-error/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-error/20 active:scale-95"
          title={t(copy.programs.btnDelete)}
          aria-label={t(copy.programs.btnDelete)}
        >
          <Trash2 size={17} aria-hidden="true" />
        </button>
        <Link
          to={`/programs/${program.id}`}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl bg-primary text-on-primary no-underline transition-colors duration-150 hover:bg-primary-container focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
          aria-label={t(copy.programs.viewDetails)}
        >
          <ChevronRight size={17} aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

export function Programs() {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<ProgramFilter>('ALL');
  const [view, setView] = useState<ProgramView>('cards');
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const { language, t } = useLanguage();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setIsSuggestOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data, isLoading, error, refetch } = usePrograms({
    page,
    limit: 12,
    search: debouncedSearch || undefined,
    status: selectedStatus === 'ALL' ? undefined : selectedStatus,
  });
  const { data: suggestionsData, isLoading: isSuggestionsLoading } = usePrograms({
    search: searchQuery.trim() || undefined,
    limit: 6,
  });
  const { mutate: deleteProgram } = useDeleteProgram();
  const programs = data?.items || [];
  const suggestedPrograms = suggestionsData?.items || [];
  const filters = useMemo(
    () => [
      { value: 'ALL' as const, label: copy.programs.filterAll },
      { value: 'PUBLISHED' as const, label: copy.programs.filterPublished },
      { value: 'DRAFT' as const, label: copy.programs.filterDraft },
      { value: 'ARCHIVED' as const, label: copy.programs.filterArchived },
    ],
    []
  );
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return t(copy.programs.dateNotSet);
    return new Date(date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  const statusLabel = (status: ProgramStatus) => {
    switch (status) {
      case 'PUBLISHED':
        return t(copy.programs.statusPublished);
      case 'DRAFT':
        return t(copy.programs.statusDraft);
      case 'ARCHIVED':
        return t(copy.programs.statusArchived);
    }
  };
  const handleDelete = (id: string) => {
    if (window.confirm(t(copy.programs.confirmDelete))) deleteProgram(id);
  };

  if (isLoading && programs.length === 0) {
    return <LoadingState />;
  }
  if (error) {
    return (
      <div className="mx-auto max-w-lg rounded-3xl bg-error-container p-6 text-on-error-container">
        <FileText size={22} aria-hidden="true" />
        <h1 className="mt-4 font-headline text-xl font-bold">
          <Bilingual {...copy.programs.errorLoadingPrograms} />
        </h1>
        <p className="mt-2 text-sm leading-6">{(error as Error).message}</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-xl bg-on-error-container px-4 text-xs font-label font-bold text-error-container transition-colors duration-150 hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-on-error-container active:scale-95"
        >
          <RefreshCw size={15} aria-hidden="true" />
          <Bilingual {...copy.programs.retry} />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      <section className="grid gap-6 rounded-[2rem] bg-surface-container-low p-6 sm:p-8 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
        <div className="max-w-3xl">
          <p className="font-label text-xs font-bold uppercase tracking-[0.14em] text-primary">
            <Bilingual {...copy.programs.workspaceLabel} />
          </p>
          <h1 className="mt-3 overflow-wrap-anywhere font-headline text-3xl font-extrabold tracking-tight text-on-surface sm:text-4xl">
            <Bilingual {...copy.programs.listTitle} />
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-on-surface-variant">
            <Bilingual {...copy.programs.listSubtitle} />
          </p>
        </div>
        <Link
          to="/programs/create"
          className="inline-flex min-h-12 items-center justify-center gap-3 self-start whitespace-nowrap rounded-xl bg-primary px-5 text-xs font-label font-bold text-on-primary no-underline transition-colors duration-150 hover:bg-primary-container focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95 xl:self-auto"
        >
          <Plus size={18} aria-hidden="true" />
          <Bilingual {...copy.programs.createBtn} />
        </Link>
      </section>

      <section className="space-y-5 rounded-3xl bg-surface-container-lowest p-5 shadow-level1 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full max-w-xl" ref={searchContainerRef}>
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant"
              aria-hidden="true"
            />
            <input
              aria-label={t(copy.programs.searchPlaceholder)}
              className="w-full rounded-2xl bg-surface-container-low py-3 pl-11 pr-10 text-sm text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20"
              placeholder={t(copy.programs.searchPlaceholder)}
              type="search"
              value={searchQuery}
              onFocus={() => setIsSuggestOpen(searchQuery.trim().length > 0)}
              onChange={event => {
                setSearchQuery(event.target.value);
                setIsSuggestOpen(event.target.value.trim().length > 0);
              }}
              onKeyDown={event => {
                if (event.key === 'Escape') setIsSuggestOpen(false);
              }}
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setIsSuggestOpen(false);
                }}
                className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-on-surface-variant transition-colors duration-150 hover:bg-surface-container-high hover:text-error focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20"
                title={t(copy.programs.clearSearch)}
                aria-label={t(copy.programs.clearSearch)}
              >
                <X size={16} aria-hidden="true" />
              </button>
            ) : isSuggestionsLoading ? (
              <Loader2 className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-on-surface-variant" />
            ) : null}
            {isSuggestOpen && searchQuery.trim().length > 0 && (
              <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-2xl bg-surface-container-lowest p-2 shadow-level4 animate-fadeIn">
                <p className="flex items-center gap-1.5 px-3 py-2 text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                  <BookOpen size={12} className="text-primary" aria-hidden="true" />
                  <Bilingual {...copy.programs.searchSuggestTitle} />
                </p>
                {suggestedPrograms.length > 0 ? (
                  suggestedPrograms.map(program => (
                    <button
                      key={program.id}
                      type="button"
                      onClick={() => {
                        setSearchQuery(program.name);
                        setIsSuggestOpen(false);
                      }}
                      className="flex w-full items-center justify-between gap-3 rounded-xl p-3 text-left transition-colors duration-150 hover:bg-surface-container-low focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-[0.99]"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-on-surface">
                          {program.name}
                        </span>
                        <span className="mt-1 block font-mono text-[10px] font-bold uppercase text-primary">
                          {program.code}
                        </span>
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    </button>
                  ))
                ) : (
                  <p className="p-4 text-center text-xs text-on-surface-variant">
                    <Bilingual {...copy.programs.noSuggestions} />
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex w-full items-center justify-between gap-2 rounded-2xl bg-surface-container-low p-1.5 lg:w-auto">
            <ViewButton
              active={view === 'cards'}
              icon={<Grid2X2 size={15} />}
              label={copy.programs.viewCards}
              onClick={() => setView('cards')}
            />
            <ViewButton
              active={view === 'rows'}
              icon={<List size={15} />}
              label={copy.programs.viewRows}
              onClick={() => setView('rows')}
            />
          </div>
        </div>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap gap-2" aria-label={t(copy.programs.filterStatusLabel)}>
            {filters.map(filter => (
              <button
                key={filter.value}
                type="button"
                onClick={() => {
                  setSelectedStatus(filter.value);
                  setPage(1);
                }}
                aria-pressed={selectedStatus === filter.value}
                className={`min-h-10 whitespace-nowrap rounded-xl px-3.5 text-xs font-bold transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95 ${selectedStatus === filter.value ? 'bg-primary text-on-primary' : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'}`}
              >
                <Bilingual {...filter.label} />
              </button>
            ))}
          </div>
          <p className="flex items-center gap-2 text-xs text-on-surface-variant" aria-live="polite">
            <Archive size={14} className="text-primary" aria-hidden="true" />
            <span className="font-semibold tabular-nums">
              {data?.meta?.total ?? programs.length}
            </span>
            <Bilingual {...copy.programs.programsShown} />
          </p>
        </div>
      </section>

      {programs.length > 0 ? (
        view === 'cards' ? (
          <section className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {(programs as Program[]).map(program => (
              <ProgramCard
                key={program.id}
                program={program}
                formatDate={formatDate}
                statusLabel={statusLabel}
                statusVariant={getStatusVariant}
                onDelete={handleDelete}
              />
            ))}
          </section>
        ) : (
          <section className="space-y-2 rounded-3xl bg-surface-container-low p-2">
            {(programs as Program[]).map(program => (
              <ProgramRow
                key={program.id}
                program={program}
                formatDate={formatDate}
                statusLabel={statusLabel}
                statusVariant={getStatusVariant}
                onDelete={handleDelete}
              />
            ))}
          </section>
        )
      ) : (
        <section className="rounded-3xl bg-surface-container-low p-8 text-center sm:p-12">
          <BookOpen className="mx-auto h-8 w-8 text-primary" aria-hidden="true" />
          <h2 className="mt-4 font-headline text-xl font-bold text-on-surface">
            <Bilingual {...copy.programs.emptyTitle} />
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-on-surface-variant">
            <Bilingual {...copy.programs.noPrograms} />
          </p>
          <Link
            to="/programs/create"
            className="mt-6 inline-flex min-h-11 items-center gap-2 whitespace-nowrap rounded-xl bg-primary px-4 text-xs font-label font-bold text-on-primary no-underline transition-colors duration-150 hover:bg-primary-container focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
          >
            <Plus size={16} aria-hidden="true" />
            <Bilingual {...copy.programs.createBtn} />
          </Link>
        </section>
      )}
      {data?.meta && data.meta.totalPages > 1 && (
        <Pagination
          currentPage={data.meta.page}
          totalPages={data.meta.totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm font-body text-on-surface-variant">
        <Bilingual {...copy.programs.loadingPrograms} />
      </p>
    </div>
  );
}

function ViewButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: { id: string; ar: string };
  onClick: () => void;
}) {
  const { t } = useLanguage();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t(label)}
      aria-pressed={active}
      className={`inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-xl px-3 text-xs font-bold transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95 lg:flex-none ${active ? 'bg-surface-container-lowest text-primary shadow-level1' : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'}`}
    >
      {icon}
      <Bilingual {...label} />
    </button>
  );
}
